import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { ARTICLES } from "./BlogPage";

const CATEGORY_COLORS = {
  Stripe: "bg-blue-100 text-blue-800 border-blue-200",
  Shopify: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Infrastructure: "bg-blue-100 text-blue-800 border-blue-200",
};

function Code({ children }) {
  return (
    <code className="px-1.5 py-0.5 bg-slate-100 text-slate-800 rounded text-sm font-mono">
      {children}
    </code>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="mb-4 px-3 py-2.5 bg-slate-100 text-slate-800 rounded-lg text-sm font-mono overflow-x-auto">
      {children}
    </pre>
  );
}

function ArticleCta({
  tier = "€249",
  message = "We fix Stripe webhook issues live in under 2 hours — a vetted expert joins your workspace, diagnoses the root cause, and fixes it while you watch.",
}) {
  return (
    <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6 sm:p-8 text-center">
      <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
        Still not working after trying all of this?
      </h3>
      <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-2 max-w-2xl mx-auto">
        {message}
      </p>
      <p className="text-slate-600 text-sm mb-6">
        Flat fee {tier}. Fixed or fully refunded within scope.
      </p>
      <Link
        to="/submit-problem"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-md text-sm sm:text-base"
      >
        Fix It Now — from {tier}
      </Link>
    </div>
  );
}

function WebhooksArticle() {
  return (
    <article className="prose prose-gray max-w-none">
      <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-6">
        You just deployed to production and your Stripe webhooks silently stopped
        firing. No errors in your dashboard. Subscriptions not activating. Checkout
        confirmations not triggering. Revenue is flowing in but your backend does
        not know about it.
      </p>
      <p className="text-slate-700 text-base leading-relaxed mb-10">
        This is one of the most common Stripe integration emergencies and it almost
        always comes down to one of three causes.
      </p>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          1. Your webhook endpoint URL changed during deployment
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          This is the most common cause. When you deploy to a new environment — a
          new Vercel URL, a new Railway service, a new server — your endpoint URL
          changes. But Stripe is still sending events to your old URL.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to Stripe Dashboard → Developers → Webhooks. Click your webhook
          endpoint. Compare the URL shown against your current deployment URL. If
          they do not match — this is your problem.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Click the endpoint in Stripe Dashboard and update the URL to your current
          production URL. Click Save. Send a test event to confirm delivery.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          2. Your webhook secret is not set in the new environment
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Stripe signs every webhook event with a secret. Your code verifies this
          signature before processing the event. If the secret is missing or wrong in
          your new environment, verification fails silently and events are ignored.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Look for errors in your server logs around{" "}
          <Code>Webhook signature verification failed</Code> or{" "}
          <Code>No signatures found matching the expected signature</Code>. Check
          your environment variables — is <Code>STRIPE_WEBHOOK_SECRET</Code> set in
          your production environment?
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Go to Stripe Dashboard → Developers → Webhooks → click your endpoint →
          reveal the signing secret. Add it to your production environment variables
          as <Code>STRIPE_WEBHOOK_SECRET</Code>.
        </p>
        <p className="text-slate-700 leading-relaxed">
          Redeploy and send a test event.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          3. Your endpoint is returning a non-200 response
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Stripe expects your webhook endpoint to return HTTP 200 within 30 seconds.
          If it returns anything else — a 500 error, a redirect, a timeout — Stripe
          marks the delivery as failed and retries later.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to Stripe Dashboard → Developers → Webhooks → click your endpoint →
          click a failed event → look at the HTTP response code and response body.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Whatever error your endpoint is returning is in your server logs. Fix the
          underlying error. Make sure your endpoint returns 200 immediately after
          receiving the event, before doing any processing (use async processing or
          a queue).
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          4. How to confirm your webhook is working
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Use the Stripe Dashboard test:
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          Stripe Dashboard → Developers → Webhooks → click endpoint → Send test event
          → choose <Code>payment_intent.succeeded</Code> → Send.
        </p>
        <p className="text-slate-700 leading-relaxed">
          Check the delivery log to confirm it shows 200 OK.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          Conclusion
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          In 90% of cases the fix is one of these three things. Check them in order
          — endpoint URL first, then webhook secret, then response code.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If you have checked all three and webhooks are still not firing, the issue
          is likely in your specific code logic and needs live debugging.
        </p>
      </section>

      <ArticleCta tier="€249" />
    </article>
  );
}

function CheckoutArticle() {
  return (
    <article className="prose prose-gray max-w-none">
      <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-6">
        Stripe Checkout works perfectly on your machine. You test it, cards
        process fine, redirects work. Then you deploy to production and it
        does not load — a blank space where the payment form should be, or a
        console error, or it loads for some users but not others.
      </p>
      <p className="text-slate-700 text-base leading-relaxed mb-10">
        This is almost always an environment or configuration issue, not a
        Stripe outage. Here are the four most common causes, in the order you
        should check them.
      </p>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          1. Publishable key is wrong or missing
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          <Code>loadStripe()</Code> needs a valid publishable key to
          initialise. If the key is missing, malformed, or a test key
          (<Code>pk_test_...</Code>) is being used in a live environment, Stripe.js
          either fails to load or refuses to process real payments.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Open the browser console. Look for{" "}
          <Code>No API key provided</Code> or{" "}
          <Code>You did not provide a valid Stripe publishable key</Code>.
          Confirm the environment variable is actually set in your production
          deployment — in React it must be prefixed{" "}
          <Code>REACT_APP_</Code>, in Next.js it must be prefixed{" "}
          <Code>NEXT_PUBLIC_</Code>, or the client bundle will never see it.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Set the correct publishable key in your hosting provider's
          environment variables, matching live or test mode to your intended
          environment. Rebuild and redeploy — env vars baked into a client
          bundle at build time do not take effect until the next build.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          2. Elements provider not wrapping the component correctly
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          <Code>CardElement</Code> and <Code>PaymentElement</Code> only work
          when rendered inside an <Code>{"<Elements>"}</Code> provider with a
          valid <Code>stripe</Code> prop. If the form is rendered outside that
          provider, or the <Code>stripePromise</Code> is recreated on every
          render instead of created once, the element mounts silently with no
          visible output.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Inspect the DOM where the card fields should be — is there an empty{" "}
          <Code>{"<div>"}</Code> or nothing at all? Check the console for{" "}
          <Code>Elements</Code> or <Code>useElements</Code> warnings about a
          missing or undefined <Code>stripe</Code> object.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Create <Code>stripePromise</Code> once, outside any component, with{" "}
          <Code>loadStripe()</Code>. Wrap the exact component tree that
          contains your card form in{" "}
          <Code>{"<Elements stripe={stripePromise}>"}</Code>, and make sure
          that provider is not conditionally unmounted while the form is
          visible.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          3. Content Security Policy blocking Stripe.js
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          If your production server sends stricter CSP headers than your local
          dev server, Stripe's script or its iframes can be blocked outright.
          The app shows no error of its own — the browser silently refuses to
          load the script.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Open the browser console on the production site and look for{" "}
          <Code>Refused to load the script 'https://js.stripe.com/...' because
          it violates the following Content Security Policy directive</Code>.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Add <Code>https://js.stripe.com</Code> to your{" "}
          <Code>script-src</Code> directive, and{" "}
          <Code>https://js.stripe.com</Code> plus{" "}
          <Code>https://hooks.stripe.com</Code> to your{" "}
          <Code>frame-src</Code> directive. Redeploy and hard-refresh to
          confirm the header change took effect.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          4. Loading in SSR context without checking for window
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          In Next.js, code that runs during server-side rendering has no{" "}
          <Code>window</Code> object. If <Code>loadStripe()</Code> or any
          Checkout initialisation logic runs at the top level of a page or
          during <Code>getServerSideProps</Code>, the server render either
          crashes or silently skips it — so Checkout works after client-side
          navigation but not on a direct page load or hard refresh.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Check your server logs for <Code>window is not defined</Code>.
          Reproduce by loading the checkout URL directly (not by clicking a
          link inside the app) — if it fails only on direct load, this is your
          cause.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed">
          Guard any browser-only code with{" "}
          <Code>{'typeof window !== "undefined"'}</Code>, move Stripe
          initialisation into a <Code>useEffect</Code> or a client component,
          or dynamically import the component with{" "}
          <Code>{"{ ssr: false }"}</Code> so it never runs on the server.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          5. How to confirm Checkout is working
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Do not test only via client-side navigation. Test all three of these:
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          A hard refresh directly on the checkout page URL. A fresh incognito
          window with no cached scripts. The Network tab showing a request to{" "}
          <Code>js.stripe.com</Code> returning <Code>200</Code>.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If all three load the card fields correctly, the fix is confirmed.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          Conclusion
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Almost every "Checkout not loading" report traces back to
          configuration, not a bug in Stripe's platform. Check the publishable
          key first, then the Elements provider, then CSP headers, then SSR
          handling.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If you have checked all four and it still fails, the issue is likely
          specific to your build pipeline or framework version and needs live
          debugging.
        </p>
      </section>

      <ArticleCta
        tier="€249"
        message="We fix Stripe Checkout integration issues live in under 2 hours — a vetted expert joins your workspace, diagnoses the root cause, and fixes it while you watch."
      />
    </article>
  );
}

function ShopifyCheckoutArticle() {
  return (
    <article className="prose prose-gray max-w-none">
      <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-6">
        You just updated your Shopify theme and now your checkout is broken.
        Orders have stopped coming in. Customers are hitting errors at the
        cart or payment stage. Every minute your checkout is down is revenue
        you will not recover.
      </p>
      <p className="text-slate-700 text-base leading-relaxed mb-10">
        Theme updates are one of the most common causes of sudden Shopify
        checkout failures. Here are the four most likely causes and exactly
        how to fix each one.
      </p>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          1. The theme update overwrote your custom checkout code
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          This is the most common cause. If you had custom code in your theme
          files — additional scripts in <Code>theme.liquid</Code>, custom
          checkout CSS, or modified snippets — a major theme update can
          overwrite those files with the new version, silently removing your
          customisations.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          In your Shopify admin go to Online Store → Themes. Click Actions →
          Edit code on your live theme. Compare your{" "}
          <Code>theme.liquid</Code> and <Code>checkout.liquid</Code> files
          against what you remember having. Look for missing scripts, removed
          sections, or changes to payment-related snippets. Shopify also
          keeps a version history — click the file and look for the "View
          file history" option to see what changed.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          If you have a backup of your previous theme (Shopify automatically
          creates one before updates — go to Themes and look for a theme
          named "Previous theme" or similar), restore your custom code from
          that backup by copying the relevant sections back into the updated
          theme.
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          If you do not have a backup, you will need to reapply your
          customisations manually to the new theme version.
        </p>
        <p className="text-slate-700 leading-relaxed">
          Going forward: always duplicate your theme before updating it.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          2. A third-party app is conflicting with the new theme version
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Shopify apps inject code into your theme. When the theme updates,
          that injected code can conflict with the new theme structure —
          breaking the cart, the checkout button, or the payment page.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to Online Store → Themes → Actions → Edit code. Look in{" "}
          <Code>theme.liquid</Code> for script tags or app blocks you did not
          add yourself. These are app injections. To isolate the problem: go
          to Apps in your Shopify admin and temporarily disable apps one by
          one, checking your checkout after each one. Start with recently
          installed apps or apps that modify the cart and checkout.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Once you identify the conflicting app, contact the app developer —
          most have support teams who can update their integration for the
          new theme version.
        </p>
        <p className="text-slate-700 leading-relaxed">
          As a temporary fix, disable the conflicting app until the developer
          provides a compatible update.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          3. Your payment gateway settings got reset during the update
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          In rare cases a theme update can trigger a refresh of your payment
          configuration, particularly if you are using a custom payment
          gateway or third-party payment provider integrated through your
          theme rather than Shopify's native payments.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to Settings → Payments in your Shopify admin. Confirm your
          payment provider is still active and connected. Look for any
          warning banners or disconnected status indicators. Try placing a
          test order using Shopify's test payment gateway (Settings →
          Payments → enable test mode) to confirm whether the issue is at the
          payment stage specifically.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          If your payment provider shows as disconnected, reconnect it by
          going through the provider's setup flow again in Settings →
          Payments.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If you are using Shopify Payments, make sure your account is still
          verified and no alerts are showing in the Payments settings page.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          4. A Liquid template error is breaking the cart or checkout page
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Shopify themes use Liquid templating. If the theme update
          introduced a Liquid syntax error — or if your existing custom
          Liquid code is incompatible with the new theme version — the cart
          or checkout page can fail to render correctly.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Open your browser developer tools (F12) and go to the Console tab.
          Load your cart page and checkout page and look for JavaScript
          errors or failed resource loads. In Shopify admin go to Online
          Store → Themes → Actions → Edit code. Look at{" "}
          <Code>cart.liquid</Code>, <Code>checkout.liquid</Code>, and any
          custom snippet files for syntax errors — Shopify's code editor
          highlights errors in red. You can also preview your theme and
          navigate to the cart — if you see a blank page or partial render, a
          Liquid error is likely.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Find the file containing the error (the editor highlights it). If
          the error is in your custom code, fix the Liquid syntax.
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          If the error is in the theme's own files, check the theme
          developer's changelog or support documentation — this may be a
          known bug in the update that has a patch available.
        </p>
        <p className="text-slate-700 leading-relaxed">
          As a last resort, restore the previous version of that specific
          file from your theme backup.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          5. How to confirm your checkout is working
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          After applying any fix, verify end-to-end before declaring the
          problem resolved:
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to Settings → Payments → enable Shopify's test gateway. Add a
          product to your cart and go through the full checkout flow using
          test payment details. Confirm the order appears in your Orders
          section in Shopify admin. Disable the test gateway and re-enable
          your live payment provider.
        </p>
        <p className="text-slate-700 leading-relaxed">
          Place one final real test order with a real card.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          Conclusion
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Theme updates breaking checkout almost always comes down to one of
          these four causes. Check them in order — custom code first, then
          app conflicts, then payment gateway settings, then Liquid errors.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If you have checked all four and your checkout is still broken, the
          issue is likely a specific interaction between your theme
          configuration and your store setup that needs live debugging.
        </p>
      </section>

      <ArticleCta
        tier="€149"
        message="We fix Shopify checkout issues live in under 2 hours — a vetted expert joins your workspace, diagnoses the root cause, and fixes it while you watch."
      />
    </article>
  );
}

function SslCertificateArticle() {
  return (
    <article className="prose prose-gray max-w-none">
      <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-6">
        Your site is showing a security warning. Visitors see "Your
        connection is not private" or "This site is not secure" and cannot
        proceed. Chrome, Firefox, and Safari all block access with a red
        warning page.
      </p>
      <p className="text-slate-700 text-base leading-relaxed mb-10">
        Every minute this warning is showing, you are losing visitors,
        customers, and revenue. Search engines also demote sites with SSL
        errors. This needs to be fixed immediately. Here is the fastest way
        to identify the problem and fix it depending on where your site is
        hosted.
      </p>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          1. Confirm your SSL certificate has actually expired
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          Before fixing anything, confirm the certificate is the problem and
          not something else like a misconfigured redirect or a mixed content
          issue.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Click the warning in your browser and look for details. Chrome
          shows "Your connection is not private" with an error code —{" "}
          <Code>NET::ERR_CERT_DATE_INVALID</Code> means the certificate has
          expired. <Code>NET::ERR_CERT_AUTHORITY_INVALID</Code> means a
          different problem (wrong certificate authority).
        </p>
        <p className="text-slate-700 leading-relaxed">
          You can also check at{" "}
          <a
            href="https://www.ssllabs.com/ssltest/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ssllabs.com/ssltest
          </a>
          . Enter your domain and run the test. It will show exactly when
          your certificate expired and what the error is.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          2. Fix it on Vercel, Netlify, or Railway
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          These platforms manage SSL certificates automatically using Let's
          Encrypt. If your certificate expired on one of these platforms it
          usually means automatic renewal failed — almost always because of a
          DNS misconfiguration.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-4">
          Go to your project dashboard on Vercel, Netlify, or Railway. Look
          for a Domains or SSL section. It will show the certificate status
          and any errors.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          In Vercel: go to Project Settings → Domains → click your domain →
          click Refresh Certificate.
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          In Netlify: go to Site Settings → Domain Management → HTTPS →
          click Renew Certificate.
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          In Railway: go to Settings → Domains → your custom domain — the
          certificate should auto-renew within minutes of DNS being correct.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If renewal fails, check that your domain's DNS A record or CNAME
          record still points to the platform. If you recently changed DNS
          settings this is the most likely cause.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          3. Fix it on a VPS or dedicated server
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          If you manage your own server (DigitalOcean, AWS EC2, Hetzner,
          Linode), your SSL certificate from Let's Encrypt must be renewed
          manually or via a cron job.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to check</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          SSH into your server and run:
        </p>
        <CodeBlock>sudo certbot certificates</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-4">
          This shows all certificates and their expiry dates.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          How to fix with Certbot
        </h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Run the renewal command:
        </p>
        <CodeBlock>sudo certbot renew</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-2">
          If the certificate has already expired, force renewal:
        </p>
        <CodeBlock>sudo certbot renew --force-renewal</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-2">
          Then restart your web server:
        </p>
        <CodeBlock>{"sudo systemctl restart nginx"}</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-2">or</p>
        <CodeBlock>sudo systemctl restart apache2</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-2">
          To prevent this happening again, set up automatic renewal:
        </p>
        <CodeBlock>sudo crontab -e</CodeBlock>
        <p className="text-slate-700 leading-relaxed mb-2">Add this line:</p>
        <CodeBlock>0 0,12 * * * certbot renew --quiet</CodeBlock>
        <p className="text-slate-700 leading-relaxed">
          This runs the renewal check twice daily.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          4. Fix it on cPanel or shared hosting
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          If your site is on shared hosting (Hostinger, Bluehost, SiteGround,
          GoDaddy), SSL management is in your control panel.
        </p>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">How to fix</h3>
        <p className="text-slate-700 leading-relaxed mb-2">
          Log into your hosting control panel (cPanel or equivalent). Find
          the SSL/TLS section — usually under Security. Look for AutoSSL or
          Let's Encrypt SSL. Click Run AutoSSL or Reinstall Certificate.
        </p>
        <p className="text-slate-700 leading-relaxed mb-2">
          Most shared hosts auto-renew SSL — if yours expired it may mean
          AutoSSL is disabled or failed. Enable it and run it manually.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If your host uses a paid SSL certificate (not Let's Encrypt), you
          need to purchase a renewal from your host or from a certificate
          authority like Sectigo or DigiCert, then install it through the
          SSL/TLS section of cPanel.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          5. Verify the fix is working
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          After renewing the certificate:
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          Clear your browser cache completely (Ctrl+Shift+Delete in Chrome)
          — old SSL errors are sometimes cached. Open your site in an
          incognito window to avoid cache. Check the padlock icon in the
          browser address bar — it should show as locked with no warnings.
        </p>
        <p className="text-slate-700 leading-relaxed">
          Run the{" "}
          <a
            href="https://www.ssllabs.com/ssltest/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            SSL Labs test
          </a>{" "}
          again to confirm the certificate is valid and shows an A or A+
          rating. Check that all pages load without mixed content warnings
          (these appear when some resources like images or scripts still
          load over HTTP instead of HTTPS).
        </p>
      </section>

      <section className="mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
          Conclusion
        </h2>
        <p className="text-slate-700 leading-relaxed mb-4">
          SSL certificate renewal is almost always straightforward on modern
          hosting platforms — most handle it automatically. When it fails it
          is usually a DNS misconfiguration or an expired cron job that
          prevented auto-renewal.
        </p>
        <p className="text-slate-700 leading-relaxed mb-4">
          The fastest path: check your hosting platform's SSL section first,
          click renew or refresh, and confirm with an incognito browser
          window.
        </p>
        <p className="text-slate-700 leading-relaxed">
          If the certificate renewed but visitors still see warnings, the
          issue is likely mixed content or a CDN caching the old certificate
          — both need live debugging.
        </p>
      </section>

      <ArticleCta
        tier="€149"
        message="We fix SSL certificate and infrastructure issues live in under 2 hours — a vetted expert joins your workspace, diagnoses the root cause, and fixes it while you watch."
      />
    </article>
  );
}

function BlogPostPage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} — SkillSyncs Intelligence`;
    }
  }, [article]);

  useEffect(() => {
    if (!article) return;
    const meta =
      document.querySelector('meta[name="description"]') ||
      document.createElement('meta');
    meta.name = 'description';
    meta.content = article.excerpt;
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article not found</h1>
          <p className="text-slate-600 mb-6">
            This guide does not exist or may have been moved.
          </p>
          <Link
            to="/blog"
            className="text-blue-600 font-semibold hover:text-blue-800"
          >
            ← Back to all guides
          </Link>
        </div>
      </div>
    );
  }

  const FULL_CONTENT_BY_SLUG = {
    "stripe-webhooks-not-firing-after-deployment": WebhooksArticle,
    "stripe-checkout-not-loading-react-nextjs": CheckoutArticle,
    "shopify-checkout-broken-after-update": ShopifyCheckoutArticle,
    "ssl-certificate-expired-emergency-fix": SslCertificateArticle,
  };
  const ArticleContent = FULL_CONTENT_BY_SLUG[slug];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb + meta */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Link
            to="/blog"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium mb-4 inline-block"
          >
            ← All guides
          </Link>
          <span
            className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full border mb-4 ${
              CATEGORY_COLORS[article.category] ||
              "bg-slate-100 text-slate-800 border-slate-200"
            }`}
          >
            {article.category}
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="text-sm text-slate-500">
            <span>{article.date}</span>
            <span className="mx-2">·</span>
            <span>{article.readTime}</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">By the SkillSyncs Expert Team</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        {ArticleContent ? (
          <ArticleContent />
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-10 text-center mb-8">
              <FileText className="w-10 h-10 mx-auto mb-4 text-slate-300" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Coming soon
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                This guide is being written by our payment recovery experts.
                Check back soon, or get live help now if your payment system is
                broken today.
              </p>
            </div>
            <ArticleCta
              tier="€149"
              message="We fix payment and infrastructure issues live in under 2 hours — a vetted expert joins your workspace, diagnoses the root cause, and fixes it while you watch."
            />
          </>
        )}
      </div>
    </div>
  );
}

export default BlogPostPage;
